<template>
  <!-- Debug info for developers only -->
  <div class="d-none">
    Options: {{ day_report_options.length }} Permissions:
    {{
      JSON.stringify({
        can_edit: props.day_report.can_edit,
        can_open: props.day_report.can_open,
        can_close: props.day_report.can_close,
        can_destroy: props.day_report.can_destroy,
      })
    }}
  </div>

  <!-- Show menu with fixed styling -->
  <div class="menu-wrapper">
    <v-menu>
      <template #activator="{ props: menuProps }">
        <v-btn
          color="primary"
          variant="tonal"
          size="small"
          v-bind="menuProps"
          class="action-menu-btn"
        >
          <v-icon class="me-2">mdi-dots-vertical</v-icon>
          Acties
        </v-btn>
      </template>

      <v-list density="compact">
        <v-list-item v-if="props.day_report.can_edit" @click="editDayReport()">
          <template #prepend>
            <v-icon color="primary">mdi-playlist-edit</v-icon>
          </template>
          <v-list-item-title>Dagrapport wijzigen</v-list-item-title>
        </v-list-item>

        <v-list-item v-if="props.day_report.can_open" @click="openDayReport()">
          <template #prepend>
            <v-icon color="primary">mdi-folder-open</v-icon>
          </template>
          <v-list-item-title>Dagrapport openen</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="props.day_report.can_close"
          @click="closeDayReport()"
        >
          <template #prepend>
            <v-icon color="primary">mdi-folder-zip</v-icon>
          </template>
          <v-list-item-title>Dagrapport sluiten</v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="props.day_report.can_destroy"
          @click="destroyDayReport()"
        >
          <template #prepend>
            <v-icon color="error">mdi-delete</v-icon>
          </template>
          <v-list-item-title>Dagrapport verwijderen</v-list-item-title>
        </v-list-item>

        <!-- Add a manual button if no permissions -->
        <v-list-item v-if="!day_report_options.length" @click="editDayReport()">
          <template #prepend>
            <v-icon color="primary">mdi-playlist-edit</v-icon>
          </template>
          <v-list-item-title>Dagrapport wijzigen</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useFormsStore } from "@/stores/formsStore";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import axios from "axios";

const props = defineProps({
  day_report: {
    type: Object,
    required: true,
  },
});

const router = useRouter();
const formsStore = useFormsStore();
const dayReportsStore = useDayReportsStoreRefactor();
const alertStore = useAlertStore();
const authStore = useAuthStoreRefactor();

const day_report_options = computed(() => {
  let options = [];

  console.log("DayReportOptions - day_report:", props.day_report);
  console.log("DayReportOptions - permissions:", {
    can_edit: props.day_report.can_edit,
    can_open: props.day_report.can_open,
    can_close: props.day_report.can_close,
    can_destroy: props.day_report.can_destroy,
  });

  if (props.day_report.can_edit) {
    options.push({
      name: "Dagrapport wijzigen",
      action: editDayReport,
      icon: "mdi-playlist-edit",
    });
  }

  if (props.day_report.can_open) {
    options.push({
      name: "Dagrapport openen",
      action: openDayReport,
      icon: "mdi-folder-open",
    });
  }

  if (props.day_report.can_close) {
    options.push({
      name: "Dagrapport sluiten",
      action: closeDayReport,
      icon: "mdi-folder-zip",
    });
  }

  if (props.day_report.can_destroy) {
    options.push({
      name: "Dagrapport verwijderen",
      action: destroyDayReport,
      icon: "mdi-delete",
    });
  }

  return options;
});

function editDayReport() {
  try {
    console.log("Editing day report:", props.day_report);

    // Make sure we're passing a proper object
    const dayReportCopy = { ...props.day_report };

    formsStore.edit({
      type: "day_report",
      item: dayReportCopy,
      options: { plan_mode: false },
    });
  } catch (error) {
    console.error("Error opening day report for editing:", error);
    alertStore.error(
      "Er is een probleem opgetreden bij het openen van het dagrapport voor bewerking",
    );
  }
}

function closeDayReport() {
  alertStore.info("Dagrapport wordt gesloten...");

  console.log(
    "DayReportOptions: Attempting to close day report:",
    props.day_report.id,
  );

  // Get the token from localStorage
  const token = localStorage.getItem("token");

  // Use direct axios call instead of going through the store
  const headers = {
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  console.log(
    "Making direct axios request to:",
    `/api/v1/day_reports/${props.day_report.id}/close`,
  );
  console.log("With headers:", headers);

  // Make direct axios call
  axios
    .get(`/api/v1/day_reports/${props.day_report.id}/close`, { headers })
    .then((response) => {
      console.log(
        "DayReportOptions: Day report closed successfully via direct axios call:",
        response.data,
      );
      authStore.getCurrentUser(true);
      alertStore.success("Dagrapport succesvol gesloten");

      // Update the store to stay in sync
      dayReportsStore.addOrUpdate(response.data);
    })
    .catch((error) => {
      console.error(
        "DayReportOptions: Error closing day report via direct axios call:",
        error,
      );
      if (error.response) {
        console.error("API error response:", error.response.data);
        console.error("Response status:", error.response.status);
        console.error("Response headers:", error.response.headers);
        // Display more specific error message if available
        const errorMsg =
          error.response.data?.error ||
          error.response.data?.message ||
          "Er is een probleem opgetreden bij het sluiten van het dagrapport";
        alertStore.error(errorMsg);
      } else if (error.request) {
        console.error("No response received. Request details:", error.request);
        alertStore.error(
          "Geen reactie ontvangen van de server bij het sluiten van het dagrapport",
        );
      } else {
        console.error("Error before making request:", error.message);
        alertStore.error(
          `Fout bij het maken van het verzoek: ${error.message}`,
        );
      }
    });
}

function openDayReport() {
  alertStore.info("Dagrapport wordt geopend...");

  dayReportsStore
    .open(props.day_report)
    .then(() => {
      alertStore.success("Dagrapport succesvol geopend");
      authStore.getCurrentUser(true);
    })
    .catch((error) => {
      console.error("Error opening day report:", error);
      alertStore.error(
        "Er is een probleem opgetreden bij het openen van het dagrapport",
      );
    });
}

function destroyDayReport() {
  if (
    confirm(
      "Weet je zeker dat je dit dagrapport wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.",
    )
  ) {
    alertStore.info("Dagrapport wordt verwijderd...");

    dayReportsStore
      .destroy(props.day_report)
      .then(async () => {
        alertStore.success("Dagrapport succesvol verwijderd");

        // Refresh current user to update active_day_report_id
        // If this was the active day_report, it will now be null
        await authStore.getCurrentUser(true);

        // Check if we're on the dashboard and the deleted report was active
        if (
          router.currentRoute.value.path === "/" &&
          !authStore.user?.active_day_report_id
        ) {
          // Stay on dashboard - it will switch to form view automatically
          console.log(
            "Deleted active day_report - staying on dashboard to show form",
          );
        } else {
          // Navigate to day_reports list
        router.push("/day_reports");
        }
      })
      .catch((error) => {
        console.error("Error deleting day report:", error);
        alertStore.error(
          "Er is een probleem opgetreden bij het verwijderen van het dagrapport",
        );
      });
  }
}
</script>

<style scoped>
.menu-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-menu-btn {
  min-width: 90px;
}
</style>
